import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import type { Finding } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, pct, riskTone, thaiDateTime } from '../lib/format'

/**
 * Real-time inference (part 69).
 *
 * The server owns the model and the arithmetic. A version is a stored row with a bias, one
 * weight per feature and a baseline mean; the page only chooses a finding and a version, then
 * renders the explanation the server persisted. It never re-scores on the client, because a
 * number on this screen must be the same number an auditor sees when reading the run history
 * later, even after a newer version has been activated.
 *
 * The contribution unit differs by model family and the page says so: a linear model's numbers
 * are points, a logistic model's are log-odds. Showing both with a percent sign would be a
 * reporting error, not a cosmetic one.
 */

type ModelKind = 'linear' | 'logistic'

interface FeatureWeight {
  key: string
  weight: number
  baseline_mean: number
}

interface ModelView {
  id: string
  name: string
  version: number
  kind: ModelKind
  kind_label: string
  bias: number
  threshold: number
  drift_threshold: number
  description: string
  baseline_samples: number
  active: boolean
  status_label: string
  threshold_label: string
  feature_count: number
  version_label: string
  features: FeatureWeight[]
  created_at: string
  updated_at: string
}

interface Contribution {
  key: string
  label: string
  value: number
  weight: number
  contribution: number
  baseline_mean: number
}

interface ScoreResult {
  finding_id: string
  rule_code: string
  score: number
  raw_score: number
  probability?: number
  flagged: boolean
  contributions: Contribution[]
  features: Record<string, number>
  drift_score: number
  latency_micros: number
}

interface Latency {
  count: number
  p50_micros: number
  p95_micros: number
  mean_micros: number
  min_micros: number
  max_micros: number
}

interface DriftFeature {
  key: string
  label: string
  baseline_mean: number
  batch_mean: number
  relative_change: number
  drifted: boolean
}

interface DriftReport {
  checked: boolean
  drifted: boolean
  threshold: number
  sample_size: number
  max_relative_change: number
  features: DriftFeature[]
}

interface ScoreResponse {
  model: ModelView
  scores: ScoreResult[]
  latency: Latency
  drift: DriftReport
  as_of: string
  scored_at: string
  persisted: number
}

interface RunView {
  id: string
  model_id: string
  model_name: string
  model_version: number
  model_kind: ModelKind
  finding_id: string
  finding_rule_code: string
  score: number
  raw_score: number
  probability?: number
  flagged: boolean
  contributions: Contribution[]
  features: Record<string, number>
  drift_score: number
  latency_micros: number
  scored_by?: string
  kind_label: string
  result_label: string
  created_at: string
}

interface RunListResponse {
  runs: RunView[]
  latency: Latency
  model_id?: string
  scope: string
}

function shortID(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id
}

function latencyText(micros: number): string {
  return micros < 1000 ? `${micros} µs` : `${(micros / 1000).toFixed(2)} ms`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function resultTone(score: number): string {
  return riskTone(score)
}

export function Inference() {
  const qc = useQueryClient()
  const [findingID, setFindingID] = useState('')
  const [modelID, setModelID] = useState('')
  const [result, setResult] = useState<ScoreResponse | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [modelForm, setModelForm] = useState({
    name: 'risk-linear',
    version: '2',
    kind: 'linear' as ModelKind,
    threshold: '',
    drift_threshold: '',
    description: '',
    baseline_samples: '100',
    activate: true,
  })

  const models = useQuery({
    queryKey: ['inference-models'],
    queryFn: () => list<ModelView>('/v1/inference/models?include_inactive=true'),
    refetchInterval: 60_000,
  })
  const runs = useQuery({
    queryKey: ['inference-runs'],
    queryFn: () => api<RunListResponse>('/v1/inference/runs?limit=100'),
    refetchInterval: 30_000,
  })
  const findings = useQuery({
    queryKey: ['inference-findings'],
    queryFn: () => list<Finding>('/v1/findings?limit=100'),
    refetchInterval: 120_000,
  })

  const modelRows = models.data ?? []
  const runRows = runs.data?.runs ?? []
  const findingRows = findings.data ?? []
  const activeModel = modelRows.find((m) => m.active)
  const chosenModel = modelRows.find((m) => m.id === modelID) ?? activeModel
  const chosenFinding = findingRows.find((f) => f.id === findingID) ?? findingRows[0]
  const latency = runs.data?.latency

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['inference-runs'] })
    void qc.invalidateQueries({ queryKey: ['inference-models'] })
  }

  const score = useMutation({
    mutationFn: () => apiWrite<ScoreResponse>('/v1/inference/score', 'POST', {
      finding_id: chosenFinding?.id,
      model_id: chosenModel?.id ?? undefined,
    }),
    onSuccess: (data) => {
      setActionError(null)
      setResult(data)
      const first = data.scores[0]
      const label = first?.flagged ? 'เสี่ยงสูง' : 'ปกติ'
      setNotice(
        `ให้คะแนน ${number(data.persisted)} ข้อตรวจพบด้วย ${data.model.version_label} · ` +
        `ผลแรก ${first ? first.score.toFixed(2) : '0.00'} คะแนน (${label}) · ` +
        `p50 ${latencyText(data.latency.p50_micros)} · p95 ${latencyText(data.latency.p95_micros)}`,
      )
      invalidate()
    },
    onError: (err) => {
      setNotice(null)
      setActionError(errorText(err, 'ให้คะแนนไม่สำเร็จ'))
    },
  })

  const activate = useMutation({
    mutationFn: (id: string) => apiWrite<{ model: ModelView; activated: boolean }>(
      `/v1/inference/models/${id}/activate`, 'POST', {}),
    onSuccess: (data) => {
      setActionError(null)
      setNotice(`เปิดใช้งาน ${data.model.version_label} แล้ว`)
      setModelID(data.model.id)
      invalidate()
    },
    onError: (err) => {
      setNotice(null)
      setActionError(errorText(err, 'เปิดใช้งานโมเดลไม่สำเร็จ'))
    },
  })

  const createModel = useMutation({
    mutationFn: () => apiWrite<ModelView>('/v1/inference/models', 'POST', {
      name: modelForm.name,
      version: Number(modelForm.version),
      kind: modelForm.kind,
      threshold: modelForm.threshold === '' ? undefined : Number(modelForm.threshold),
      drift_threshold: modelForm.drift_threshold === '' ? undefined : Number(modelForm.drift_threshold),
      description: modelForm.description,
      baseline_samples: Number(modelForm.baseline_samples),
      activate: modelForm.activate,
    }),
    onSuccess: (data) => {
      setActionError(null)
      setNotice(`ลงทะเบียน ${data.version_label} แล้ว · น้ำหนักเริ่มต้นถูกบันทึกเป็นแถวในฐานข้อมูล`)
      setModelID(data.id)
      setModelForm((prev) => ({ ...prev, version: String(data.version + 1) }))
      invalidate()
    },
    onError: (err) => {
      setNotice(null)
      setActionError(errorText(err, 'ลงทะเบียนโมเดลไม่สำเร็จ'))
    },
  })

  const submitModel = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    createModel.mutate()
  }

  const latest = result?.scores[0]
  const maxContribution = latest
    ? Math.max(...latest.contributions.map((c) => Math.abs(c.contribution)), 0.0001)
    : 1

  return (
    <section className="page">
      <PageHead
        title="การให้คะแนนด้วยโมเดล"
        sub="ให้คะแนนข้อตรวจพบด้วยโมเดลเชิงเส้นหรือลอจิสติกที่มีเวอร์ชันและน้ำหนักเก็บอยู่ในฐานข้อมูล พร้อมบันทึกคุณลักษณะ การมีส่วนร่วมของแต่ละคุณลักษณะ และเวลาในการประมวลผลของทุกครั้ง"
        actions={
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={() => score.mutate()}
            disabled={score.isPending || !chosenFinding || !chosenModel}
          >
            {score.isPending ? 'กำลังให้คะแนน...' : 'ให้คะแนนข้อตรวจพบ'}
          </button>
        }
      />

      <div className="grid-4 stagger">
        <Stat
          label="โมเดลที่ลงทะเบียน"
          value={number(modelRows.length)}
          unit="รุ่น"
          foot={activeModel ? `ใช้งาน: ${activeModel.version_label}` : 'ยังไม่มีรุ่นที่เปิดใช้งาน'}
        />
        <Stat
          label="ประเภทโมเดล"
          value={activeModel ? activeModel.kind_label : '—'}
          foot={activeModel ? activeModel.threshold_label : 'ลงทะเบียนรุ่นแรกเพื่อเริ่มให้คะแนน'}
        />
        <Stat
          label="คะแนนที่บันทึก"
          value={number(latency?.count ?? 0)}
          unit="รายการ"
          foot={runs.data?.scope ?? 'รอข้อมูล'}
        />
        <Stat
          label="เวลาคำนวณ p95"
          value={latency ? latencyText(latency.p95_micros) : '—'}
          foot={latency ? `p50 ${latencyText(latency.p50_micros)} · เฉลี่ย ${latencyText(latency.mean_micros)} · ไม่รวม I/O` : 'ยังไม่มีข้อมูลเวลา'}
        />
      </div>

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald)',
            borderRadius: 6, color: 'var(--color-emerald)',
          }}
        >
          {notice}
        </div>
      ) : null}
      {actionError ? <div className="form-error" style={{ marginTop: 16 }}>{actionError}</div> : null}

      {models.isLoading || findings.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={220} /><Skel h={160} /></div>
      ) : models.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(models.error, 'โหลดโมเดลไม่สำเร็จ')}</div>
        </div>
      ) : modelRows.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่มีโมเดล"
            body="ลงทะเบียนเวอร์ชันแรกด้านล่าง ระบบจะบันทึกน้ำหนักเริ่มต้นแปดคุณลักษณะเป็นแถวใน platform.inference_features แล้วจึงให้คะแนนได้"
          />
        </div>
      ) : (
        <>
          <div className="grid-2" style={{ marginTop: 16, alignItems: 'start' }}>
            <div className="card card-pad">
              <div className="card-label">ให้คะแนนข้อตรวจพบ</div>
              <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 14px' }}>
                เลือกข้อตรวจพบและเวอร์ชันโมเดล ระบบจะคำนวณคุณลักษณะแบบกำหนดได้แน่นอน บันทึกผลลงประวัติ
                และคืนค่าการมีส่วนร่วมของทุกคุณลักษณะ เรียงจากผลกระทบมากไปน้อย
              </p>
              <div className="stack" style={{ gap: 12 }}>
                <label className="field">
                  <span className="field-label">ข้อตรวจพบ</span>
                  <select
                    className="input"
                    value={chosenFinding?.id ?? ''}
                    onChange={(e) => setFindingID(e.target.value)}
                  >
                    {findingRows.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.rule_code} · {f.severity} · {shortID(f.id)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">เวอร์ชันโมเดล</span>
                  <select
                    className="input"
                    value={chosenModel?.id ?? ''}
                    onChange={(e) => setModelID(e.target.value)}
                  >
                    {modelRows.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.version_label} · {m.kind_label} · {m.active ? 'ใช้งาน' : 'ปิด'}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.8 }}>
                  คุณลักษณะ: คะแนนความเสี่ยง · ความเชื่อมั่น · ระดับความรุนแรง · มูลค่า · ความใหม่ ·
                  จำนวนหลักฐาน · น้ำหนักกฎตั้งต้น · แรงกดดันจากสถานะ
                </div>
                {chosenModel ? (
                  <div className="dim" style={{ fontSize: 11.5 }}>
                    เกณฑ์ตัดสิน {chosenModel.threshold_label} · drift threshold{' '}
                    {(chosenModel.drift_threshold * 100).toFixed(0)}%
                  </div>
                ) : null}
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">ผลล่าสุด</div>
              {latest ? (
                <>
                  <div className="row" style={{ alignItems: 'baseline', gap: 14, marginTop: 10 }}>
                    <span className={`stat-value ${resultTone(latest.score)}`} style={{ fontSize: 40 }}>
                      {latest.score.toFixed(2)}
                    </span>
                    <span className="dim" style={{ fontSize: 12 }}>จาก 100 คะแนน</span>
                    <span className={`chip ${latest.flagged ? 'sev-high' : 'sev-low'}`} style={{ marginLeft: 'auto' }}>
                      {latest.flagged ? 'เสี่ยงสูง' : 'ปกติ'}
                    </span>
                  </div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                    {latest.rule_code} · {shortID(latest.finding_id)} · {result?.model.version_label}
                    {latest.probability !== undefined ? ` · ความน่าจะเป็น ${pct(latest.probability)}` : ''}
                    {' · '}{latest.latency_micros} µs
                  </div>
                  <div className="table-wrap" style={{ marginTop: 12 }}>
                    <table className="table data">
                      <thead>
                        <tr>
                          <th>คุณลักษณะ</th>
                          <th style={{ textAlign: 'right' }}>ค่า</th>
                          <th style={{ textAlign: 'right' }}>น้ำหนัก</th>
                          <th style={{ textAlign: 'right' }}>ผลกระทบ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latest.contributions.map((c) => (
                          <tr key={c.key} style={{ cursor: 'default' }}>
                            <td>
                              {c.label}
                              <div className="dim mono" style={{ fontSize: 10.5 }}>{c.key}</div>
                            </td>
                            <td className="num tnum">{c.value.toFixed(3)}</td>
                            <td className="num tnum dim">{c.weight.toFixed(2)}</td>
                            <td className="num tnum">
                              <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                                <span className="bar" style={{ width: 46 }}>
                                  <i style={{
                                    width: `${Math.round((Math.abs(c.contribution) / maxContribution) * 100)}%`,
                                    background: c.contribution >= 0 ? 'var(--color-amber)' : 'var(--color-emerald)',
                                  }} />
                                </span>
                                <span style={{ minWidth: 64, textAlign: 'right' }}>
                                  {c.contribution >= 0 ? '+' : ''}{c.contribution.toFixed(3)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="dim" style={{ fontSize: 11, lineHeight: 1.8, marginTop: 10, marginBottom: 0 }}>
                    หน่วยของผลกระทบ: {result?.model.kind === 'logistic'
                      ? 'ล็อกออดส์ (log-odds) ไม่ใช่เปอร์เซ็นต์'
                      : 'คะแนน (points)'} · bias {result?.model.bias.toFixed(2)} · ค่าดิบ {latest.raw_score.toFixed(3)}
                  </p>
                </>
              ) : (
                <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, marginTop: 10 }}>
                  ยังไม่มีผลในหน้านี้ กด “ให้คะแนนข้อตรวจพบ” เพื่อสร้างผลและบันทึกลงประวัติ
                  การให้คะแนนจะไม่แก้ไขข้อตรวจพบหรือสายโซ่เหตุการณ์ตรวจสอบ
                </p>
              )}
            </div>
          </div>

          {result ? (
            <div className="card card-pad" style={{ marginTop: 16 }}>
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">การตรวจการเคลื่อนของข้อมูล (drift)</span>
                <span className={`chip ${result.drift.drifted ? 'sev-high' : 'sev-low'}`} style={{ marginLeft: 'auto' }}>
                  {result.drift.drifted ? 'พบการเคลื่อน' : 'อยู่ในเกณฑ์'}
                </span>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                เทียบค่าเฉลี่ยของคุณลักษณะในชุดที่ให้คะแนนกับค่าเฉลี่ยฐานที่บันทึกไว้กับน้ำหนัก ·
                เกณฑ์ {(result.drift.threshold * 100).toFixed(0)}% · ตัวอย่าง {number(result.drift.sample_size)} รายการ ·
                ค่าสูงสุด {(result.drift.max_relative_change * 100).toFixed(1)}%
              </div>
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>คุณลักษณะ</th>
                      <th style={{ textAlign: 'right' }}>ค่าเฉลี่ยฐาน</th>
                      <th style={{ textAlign: 'right' }}>ค่าเฉลี่ยชุดนี้</th>
                      <th style={{ textAlign: 'right' }}>เปลี่ยนแปลง</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.drift.features.map((f) => (
                      <tr key={f.key} style={{ cursor: 'default' }}>
                        <td>{f.label}</td>
                        <td className="num tnum dim">{f.baseline_mean.toFixed(3)}</td>
                        <td className="num tnum">{f.batch_mean.toFixed(3)}</td>
                        <td className={`num tnum ${f.drifted ? 'sev-critical' : ''}`}>
                          {f.relative_change >= 0 ? '+' : ''}{(f.relative_change * 100).toFixed(1)}%
                        </td>
                        <td>
                          <span className={`chip ${f.drifted ? 'sev-high' : 'sev-low'}`}>
                            {f.drifted ? 'เคลื่อน' : 'ปกติ'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">เวอร์ชันโมเดล</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                น้ำหนักถูกล็อกหลังลงทะเบียน · เปิดใช้ได้ครั้งละหนึ่งรุ่นต่อชื่อ
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ชื่อ / รุ่น</th>
                    <th>ประเภท</th>
                    <th style={{ textAlign: 'right' }}>Bias</th>
                    <th>เกณฑ์</th>
                    <th style={{ textAlign: 'right' }}>คุณลักษณะ</th>
                    <th style={{ textAlign: 'right' }}>ตัวอย่างฐาน</th>
                    <th>สถานะ</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {modelRows.map((m) => (
                    <tr key={m.id} style={{ cursor: 'default' }}>
                      <td>
                        <span className="mono">{m.name}</span>
                        <div className="dim mono" style={{ fontSize: 10.5 }}>v{m.version} · {shortID(m.id)}</div>
                      </td>
                      <td>{m.kind_label}</td>
                      <td className="num tnum">{m.bias.toFixed(2)}</td>
                      <td className="dim">{m.threshold_label}</td>
                      <td className="num tnum">{number(m.feature_count)}</td>
                      <td className="num tnum dim">{m.baseline_samples === 0 ? '—' : number(m.baseline_samples)}</td>
                      <td>
                        <span className={`chip ${m.active ? 'sev-low' : 'dim'}`}>{m.status_label}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => activate.mutate(m.id)}
                          disabled={m.active || activate.isPending}
                        >
                          {m.active ? 'ใช้งานอยู่' : 'เปิดใช้งาน'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="card-label">ลงทะเบียนเวอร์ชันใหม่</div>
            <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 14px' }}>
              เวอร์ชันที่ลงทะเบียนแล้วแก้ไขไม่ได้ หากไม่ส่งน้ำหนัก ระบบจะใช้ค่าตั้งต้นของประเภทนั้น
              และบันทึกเป็นแถวจริงในฐานข้อมูลเพื่อให้ตรวจสอบย้อนหลังได้
            </p>
            <form onSubmit={submitModel}>
              <div className="grid-2">
                <label className="field">
                  <span className="field-label">ชื่อโมเดล</span>
                  <input
                    className="input"
                    value={modelForm.name}
                    onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                    maxLength={120}
                    required
                  />
                </label>
                <label className="field">
                  <span className="field-label">รุ่น (version)</span>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={modelForm.version}
                    onChange={(e) => setModelForm({ ...modelForm, version: e.target.value })}
                    required
                  />
                </label>
                <label className="field">
                  <span className="field-label">ประเภท</span>
                  <select
                    className="input"
                    value={modelForm.kind}
                    onChange={(e) => setModelForm({ ...modelForm, kind: e.target.value as ModelKind })}
                  >
                    <option value="linear">เชิงเส้น (คะแนน 0–100)</option>
                    <option value="logistic">ลอจิสติก (ความน่าจะเป็น)</option>
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">เกณฑ์ตัดสิน (เว้นว่างเพื่อใช้ค่าตั้งต้น)</span>
                  <input
                    className="input"
                    value={modelForm.threshold}
                    onChange={(e) => setModelForm({ ...modelForm, threshold: e.target.value })}
                    placeholder={modelForm.kind === 'logistic' ? '0.65' : '55'}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Drift threshold (0.01–10)</span>
                  <input
                    className="input"
                    value={modelForm.drift_threshold}
                    onChange={(e) => setModelForm({ ...modelForm, drift_threshold: e.target.value })}
                    placeholder="0.35"
                  />
                </label>
                <label className="field">
                  <span className="field-label">จำนวนตัวอย่างฐาน</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    value={modelForm.baseline_samples}
                    onChange={(e) => setModelForm({ ...modelForm, baseline_samples: e.target.value })}
                  />
                </label>
              </div>
              <label className="field" style={{ marginTop: 12 }}>
                <span className="field-label">คำอธิบาย</span>
                <input
                  className="input"
                  value={modelForm.description}
                  onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })}
                  maxLength={500}
                  placeholder="ที่มาและช่วงเวลาของข้อมูลที่ใช้กำหนดน้ำหนัก"
                />
              </label>
              <div className="row" style={{ marginTop: 14, gap: 12 }}>
                <label className="row" style={{ gap: 8, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={modelForm.activate}
                    onChange={(e) => setModelForm({ ...modelForm, activate: e.target.checked })}
                  />
                  เปิดใช้งานรุ่นนี้ทันที
                </label>
                <button className="btn btn-primary btn-sm" type="submit" disabled={createModel.isPending}>
                  {createModel.isPending ? 'กำลังลงทะเบียน...' : 'ลงทะเบียนเวอร์ชัน'}
                </button>
              </div>
            </form>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ประวัติการให้คะแนน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                p50 {latency ? latencyText(latency.p50_micros) : '—'} ·
                p95 {latency ? latencyText(latency.p95_micros) : '—'} ·
                วัดเฉพาะการคำนวณ · เก็บถาวรแบบเพิ่มอย่างเดียว
              </span>
            </div>
            {runs.isLoading ? (
              <div style={{ marginTop: 12 }}><Skel h={160} /></div>
            ) : runs.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>{errorText(runs.error, 'โหลดประวัติไม่สำเร็จ')}</div>
            ) : runRows.length === 0 ? (
              <p className="dim" style={{ fontSize: 12.5, marginTop: 12 }}>ยังไม่มีประวัติการให้คะแนน</p>
            ) : (
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>โมเดล</th>
                      <th>กฎ</th>
                      <th style={{ textAlign: 'right' }}>คะแนน</th>
                      <th>ผล</th>
                      <th style={{ textAlign: 'right' }}>Drift</th>
                      <th style={{ textAlign: 'right' }}>เวลา</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runRows.map((r) => (
                      <tr key={r.id} style={{ cursor: 'default' }}>
                        <td className="dim">{thaiDateTime(r.created_at)}</td>
                        <td>
                          <span className="mono">{r.model_name}</span>
                          <div className="dim mono" style={{ fontSize: 10.5 }}>v{r.model_version} · {r.kind_label}</div>
                        </td>
                        <td>
                          <span className="mono">{r.finding_rule_code}</span>
                          <div className="dim mono" style={{ fontSize: 10.5 }}>{shortID(r.finding_id)}</div>
                        </td>
                        <td className={`num tnum ${resultTone(r.score)}`}>{r.score.toFixed(2)}</td>
                        <td>
                          <span className={`chip ${r.flagged ? 'sev-high' : 'sev-low'}`}>{r.result_label}</span>
                        </td>
                        <td className="num tnum dim">{(r.drift_score * 100).toFixed(1)}%</td>
                        <td className="num tnum dim">{r.latency_micros} µs</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="card-label">ข้อจำกัดที่ควรรู้</div>
            <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
              <li>คะแนนคำนวณจากคุณลักษณะที่จำกัดช่วง 0–1 และน้ำหนักที่บันทึกไว้ ไม่มีการสุ่มหรือเรียกบริการภายนอก</li>
              <li>โมเดลเชิงเส้นให้คะแนนเป็น points; โมเดลลอจิสติกให้ความน่าจะเป็นและแปลงเป็นคะแนน 0–100</li>
              <li>การเปิดใช้งานรุ่นใหม่ไม่แก้ไขประวัติเดิม เพราะแต่ละ run บันทึกชื่อ รุ่น และประเภทของโมเดลไว้แล้ว</li>
              <li>drift ตรวจจากค่าเฉลี่ยของคุณลักษณะในชุดที่ให้คะแนน เทียบค่าเฉลี่ยฐานที่เก็บกับน้ำหนัก ไม่ใช่การตรวจสอบประสิทธิภาพของโมเดล</li>
              <li>หน้าเว็บไม่แก้ไขข้อตรวจพบและไม่เขียนเหตุการณ์ตรวจสอบ การให้คะแนนจึงไม่เปลี่ยนสายโซ่เหตุการณ์</li>
            </ul>
          </div>
        </>
      )}
    </section>
  )
}
